<template>
    <div>
        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog class="relative z-50 lg:hidden" @close="sidebarOpen = false">
                <TransitionChild
                    as="template"
                    enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-gray-900/80" />
                </TransitionChild>

                <div class="fixed inset-0 flex">
                    <TransitionChild
                        as="template"
                        enter="transition ease-in-out duration-300 transform"
                        enter-from="-translate-x-full"
                        enter-to="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leave-from="translate-x-0"
                        leave-to="-translate-x-full"
                    >
                        <DialogPanel class="relative mr-16 flex w-full max-w-xs md:max-w-sm flex-1">
                            <TransitionChild
                                as="template"
                                enter="ease-in-out duration-300"
                                enter-from="opacity-0"
                                enter-to="opacity-100"
                                leave="ease-in-out duration-300"
                                leave-from="opacity-100"
                                leave-to="opacity-0"
                            >
                                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                                    <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                                        <span class="sr-only">Close sidebar</span>
                                        <FontAwesomeIcon :icon="['fas', 'xmark']" class="h-5 w-5 text-white" />
                                    </button>
                                </div>
                            </TransitionChild>
                            <!-- Sidebar component, swap this element with another sidebar if you like -->
                            <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                                <div class="flex h-16 shrink-0 items-center">
                                    <img
                                        @click="$router.push(locale && locale !== 'en' ? `/${locale}` : '/')"
                                        class="h-8 w-auto"
                                        :src="whaleden"
                                        alt="Whaleden"
                                    />
                                </div>
                                <nav class="flex flex-1 flex-col">
                                    <ul role="list" class="flex flex-1 flex-col gap-y-7">
                                        <li>
                                            <ul role="list" class="-mx-2 space-y-1">
                                                <li v-for="item in navigation" :key="item.name">
                                                    <NuxtLinkLocale
                                                        v-if="!item.children"
                                                        :to="item.path"
                                                        :class="[
                                                            item.current ? 'bg-gray-50' : 'hover:bg-gray-50',
                                                            'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700',
                                                        ]"
                                                    >
                                                        {{ item.name }}
                                                    </NuxtLinkLocale>
                                                    <Disclosure as="div" v-else v-slot="{ open }">
                                                        <DisclosureButton
                                                            :class="[
                                                                item.current ? 'bg-gray-50' : 'hover:bg-gray-50',
                                                                'flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm font-semibold leading-6 text-gray-700',
                                                            ]"
                                                        >
                                                            <FontAwesomeIcon :icon="item.icon" class="w-4 h-4 mr-2" />
                                                            {{ item.name }}
                                                            <FontAwesomeIcon
                                                                :icon="['fas', 'chevron-right']"
                                                                :class="[
                                                                    open
                                                                        ? 'rotate-90 transition-all duration-1000 text-gray-500'
                                                                        : 'text-gray-400',
                                                                    'ml-auto h-3 w-3 transition-all duration-1000 shrink-0',
                                                                ]"
                                                                aria-hidden="true"
                                                            />
                                                        </DisclosureButton>
                                                        <DisclosurePanel as="ul" class="mt-1 px-1">
                                                            <li
                                                                class="flex items-center ml-9"
                                                                v-for="subItem in item.children"
                                                                :key="subItem.name"
                                                            >
                                                                <FontAwesomeIcon
                                                                    :icon="subItem.icon"
                                                                    class="w-4 h-4 mr-2"
                                                                />
                                                                <NuxtLinkLocale
                                                                    as="a"
                                                                    :to="subItem.path"
                                                                    :class="[
                                                                        'block rounded-md py-2 pl-2 pr-2 text-sm leading-6 text-gray-700',
                                                                    ]"
                                                                >
                                                                    {{ subItem.name }}
                                                                </NuxtLinkLocale>
                                                            </li>
                                                        </DisclosurePanel>
                                                    </Disclosure>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Static sidebar for desktop -->
        <div
            :class="!hasSidebar ? 'lg:hidden' : 'lg:fixed'"
            class="hidden lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col"
        >
            <div
                class="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-gradient-to-b from-white to-[#ECF3FA] px-6 pb-4"
            >
                <div
                    @click="$router.push(locale && locale !== 'en' ? `/${locale}` : '/')"
                    class="flex h-16 cursor-pointer shrink-0 items-center"
                >
                    <img class="h-8 w-auto cursor-pointer" :src="whaleden" alt="Whaleden" />
                    <p class="text-sm font-bold text-primary ml-2">Whaleden</p>
                </div>
                <nav class="flex flex-1 flex-col">
                    <ul role="list" class="flex flex-1 flex-col gap-y-7">
                        <li>
                            <ul role="list" class="-mx-2 space-y-1">
                                <FeePriorityModal v-if="dapp" v-model="priorityFeeModalOpened" />

                                <li v-for="item in navigation" :key="item.name">
                                    <NuxtLinkLocale
                                        v-if="!item.children"
                                        :to="item.path"
                                        :class="[
                                            item.current ? 'bg-gray-50' : 'hover:bg-gray-50',
                                            'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700',
                                        ]"
                                    >
                                        <span class="flex items-center">
                                            <FontAwesomeIcon :icon="item.icon" class="w-4 h-4 mr-5" /> {{ item.name }}
                                        </span>
                                    </NuxtLinkLocale>
                                    <Disclosure as="div" v-else v-slot="{ open }">
                                        <DisclosureButton
                                            :class="[
                                                item.current ? 'bg-gray-50' : 'hover:bg-gray-50',
                                                'flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm font-semibold leading-6 text-gray-700',
                                            ]"
                                        >
                                            <FontAwesomeIcon :icon="item.icon" class="w-4 h-4 mr-2" />
                                            {{ item.name }}
                                            <FontAwesomeIcon
                                                :icon="['fas', 'chevron-right']"
                                                :class="[
                                                    open
                                                        ? 'rotate-90 transition-all duration-1000 text-primary'
                                                        : 'text-secondary transition-all duration-1000',
                                                    'ml-auto h-3 w-3 shrink-0',
                                                ]"
                                                aria-hidden="true"
                                            />
                                        </DisclosureButton>
                                        <DisclosurePanel as="ul" class="mt-1 px-2">
                                            <li v-for="subItem in item.children" :key="subItem.name">
                                                <!-- 44px -->

                                                <NuxtLinkLocale
                                                    v-role="subItem.role"
                                                    as="a"
                                                    :to="subItem.path"
                                                    :class="[
                                                        subItem.current ? 'bg-gray-50' : 'hover:bg-gray-50',
                                                        'block rounded-md py-2 pl-3 pr-2 text-sm leading-6 text-gray-700',
                                                    ]"
                                                >
                                                    <FontAwesomeIcon :icon="subItem.icon" class="w-4 h-4 mr-2" />
                                                    {{ subItem.name }}
                                                </NuxtLinkLocale>
                                            </li>
                                        </DisclosurePanel>
                                    </Disclosure>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>

        <div :class="hasSidebar && 'lg:pl-72'">
            <div
                v-if="!hasSidebar"
                :class="!hasSidebar && 'shadow-sm lg:shadow-none'"
                class="sticky top-0 z-40 hidden lg:flex h-16 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-8"
            >
                <button
                    :class="!hasSidebar && 'hidden'"
                    type="button"
                    class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                    @click="sidebarOpen = true"
                >
                    <span class="sr-only">Open sidebar</span>
                    <FontAwesomeIcon :icon="['fas', 'bars']" class="h-5 text-black w-5" />
                </button>
            </div>

            <div
                v-if="hasSidebar"
                :class="'absolute right-8 top-4'"
                class="flex z-[40] flex-1 gap-x-4 justify-end self-stretch lg:gap-x-6"
            >
                <div class="flex items-center gap-x-2 lg:gap-x-2">
                    <!--  <ConnectButton /> -->
                    <AppButton @click="logout"> {{ $t('auth.signOut') }}</AppButton>
                </div>
            </div>

            <main :class="!hasSidebar && 'max-w-7xl mx-auto'" class="py-10 relative">
                <div class="px-4 sm:px-6 lg:px-6">
                    <slot />
                </div>
            </main>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import whaleden from '@/assets/img/logo.png';
    import { DataWrapper } from '@whaleden/core/types/DataWrapper';

    import {
        Dialog,
        DialogPanel,
        Disclosure,
        DisclosureButton,
        DisclosurePanel,
        TransitionChild,
        TransitionRoot,
    } from '@headlessui/vue';
    import { ref } from 'vue';

    interface NavigationItem {
        name: string;
        path: string;
        icon: string;
        role?: string[];
        current?: boolean;
    }

    interface NavigationItemWithChildren extends NavigationItem {
        children: NavigationItem[];
    }

    type NavigationConfig = NavigationItemWithChildren[];

    const home = {
        label: 'Home',
        route: '/',
    };

    const { locale } = useI18n();

    const { $keycloak } = useNuxtApp();
    const props = defineProps<{
        navigation: NavigationConfig;
        dapp?: boolean;
        hasSidebar: false;
        balanceSol?: DataWrapper<number>;
        publicKey?: string;
    }>();

    const priorityFeeModalOpened = ref(false);
    const route = useRoute();
    const breadcrumbItems = computed(() => {
        const pathSegments = route.path.split('/').filter((segment) => segment);
        const items = pathSegments.map((segment, index) => {
            const routePath = '/' + pathSegments.slice(0, index + 1).join('/');
            return {
                label: segment.charAt(0).toUpperCase() + segment.slice(1),
                route: routePath,
            };
        });

        if (items.length > 0) {
            const lastSegment = items[items.length - 1];
            if (route.params.id) {
                lastSegment.label = 'Edit';
                lastSegment.route = `${lastSegment.route}/edit`;
            } else if (route.path === '/new') {
                lastSegment.label = 'New';
                lastSegment.route = `${lastSegment.route}/new`;
            }
        }

        return items;
    });

    const logout = async () => {
        const redirectUri =
            locale.value && locale.value !== 'en'
                ? `${window.location.origin}/${locale.value}`
                : window.location.origin;
        $keycloak.logout({ redirectUri });
    };

    const hasConnectedWallet = computed(() => {
        return props.publicKey;
    });

    const sidebarOpen = ref(false);
</script>

<style>
    .router-link-exact-active {
        @apply border-b-4 border-primary text-black;
    }
</style>
